/**
 * =============================================================================
 * Hook: useAffiliateNotifications
 * Versão: 1.0.0 (Fase 3 — Bloco 3F)
 * Descrição: Lista as notificações do afiliado ativo a partir da tabela
 *   `affiliate_notifications` (RLS já filtra por affiliate_id).
 *   - Inclui contagem de não lidas (para badge do sino).
 *   - Inclui ações para marcar uma ou todas como lidas.
 *   - Realtime via Supabase será adicionado na Fase 7 (item 7.1) — aqui usamos
 *     polling leve via React Query para manter a UI atualizada sem custo extra.
 * =============================================================================
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAffiliateAuth } from '@/hooks/useAffiliateAuth';
import { createLogger } from '@/lib/logger';

const log = createLogger('useAffiliateNotifications');

export interface AffiliateNotificationRow {
  id: string;
  title: string;
  message: string | null;
  type: string;
  action_url: string | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
}

const QUERY_KEY = (id?: string) => ['affiliate-notifications', id];

export function useAffiliateNotifications(limit = 20) {
  const { affiliate, ready } = useAffiliateAuth();

  return useQuery({
    queryKey: QUERY_KEY(affiliate?.id),
    enabled: ready && !!affiliate?.id,
    // Polling leve a cada 30s — substituído por realtime na Fase 7.
    refetchInterval: 30_000,
    queryFn: async (): Promise<AffiliateNotificationRow[]> => {
      if (!affiliate?.id) return [];

      const { data, error } = await supabase
        .from('affiliate_notifications')
        .select('id, title, message, type, action_url, is_read, read_at, created_at')
        .eq('affiliate_id', affiliate.id)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        log.error('Falha ao listar affiliate_notifications', error);
        throw error;
      }
      return (data ?? []) as AffiliateNotificationRow[];
    },
    staleTime: 15_000,
  });
}

/** Mutation: marca notificações específicas (ou todas) como lidas. */
export function useMarkAffiliateNotificationsRead() {
  const { affiliate } = useAffiliateAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (ids: string[] | 'all') => {
      if (!affiliate?.id) throw new Error('Afiliado não carregado');
      const nowIso = new Date().toISOString();

      let query = supabase
        .from('affiliate_notifications')
        .update({ is_read: true, read_at: nowIso })
        .eq('affiliate_id', affiliate.id)
        .eq('is_read', false);

      if (ids !== 'all') {
        if (ids.length === 0) return;
        query = query.in('id', ids);
      }

      const { error } = await query;
      if (error) {
        log.error('Falha ao marcar notificações como lidas', error);
        throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY(affiliate?.id) });
    },
  });
}
